import { useTranslation } from 'react-i18next';
import { Checkbox, FormField, FormFieldMessage, Input, OptionCard } from '@/components/ui';
import { MIN_ADMIN_PASSWORD_LENGTH, type ResetCredential } from '../useResetCredential';
import styles from '../dialogs/accountDialogs.module.css';

interface Props {
    credential: ResetCredential;
    /** Renders the disabled "send a reset link" card — the single-account drawer only. */
    showEmailOption?: boolean;
    disabled?: boolean;
}

/**
 * EAA-1779: the "Cách đặt lại" mode picker, shared by the reset drawer (frame
 * 74) and the bulk reset dialog so the two offer the same choice with the same
 * rules.
 *
 * The temp-password card was previously a radio permanently `checked readOnly`
 * — there was only ever one mode, so it was decoration explaining the single
 * behaviour. It is a real choice now.
 */
export function ResetCredentialFields({
    credential,
    showEmailOption = false,
    disabled = false,
}: Props) {
    const { t } = useTranslation();
    const adminSet = credential.mode === 'admin_set';

    return (
        <fieldset className={styles.resetModes}>
            <legend className={styles.resetModesLegend}>{t('console.accounts.resetModal.modeLegend')}</legend>

            <OptionCard
                inputType="radio"
                name="reset_mode"
                selected={!adminSet}
                disabled={disabled}
                onSelect={() => credential.setMode('temp_password')}
                title={t('console.accounts.resetModal.optionTempTitle')}
                description={t('console.accounts.resetModal.optionTempDesc')}
            />

            <OptionCard
                inputType="radio"
                name="reset_mode"
                selected={adminSet}
                disabled={disabled}
                onSelect={() => credential.setMode('admin_set')}
                title={t('console.accounts.resetModal.optionAdminSetTitle')}
                description={t('console.accounts.resetModal.optionAdminSetDesc')}
            />

            {adminSet && (
                <div className={styles.resetPassword}>
                    <FormField label={t('console.accounts.resetModal.passwordLabel')}>
                        <Input.Password
                            value={credential.password}
                            onChange={(event) => credential.setPassword(event.target.value)}
                            placeholder={t('console.accounts.resetModal.passwordPlaceholder', {
                                count: MIN_ADMIN_PASSWORD_LENGTH,
                            })}
                            autoComplete="new-password"
                            disabled={disabled}
                        />
                    </FormField>
                    <FormField label={t('console.accounts.resetModal.confirmLabel')}>
                        <Input.Password
                            value={credential.confirm}
                            onChange={(event) => credential.setConfirm(event.target.value)}
                            autoComplete="new-password"
                            disabled={disabled}
                        />
                    </FormField>

                    <Checkbox
                        className={styles.resetForce}
                        checked={credential.forceChange}
                        disabled={disabled}
                        onChange={(event) => credential.setForceChange(event.target.checked)}
                    >
                        <span>
                            {t('console.accounts.resetModal.forceChangeLabel')}
                            <span className={styles.lockSessionsSub}>
                                {t('console.accounts.resetModal.forceChangeSub')}
                            </span>
                        </span>
                    </Checkbox>

                    <FormFieldMessage error={credential.error} />
                    <p className={styles.credModeDesc}>
                        {t('console.accounts.resetModal.adminSetNotice')}
                    </p>
                </div>
            )}

            {showEmailOption && (
                <OptionCard
                    inputType="radio"
                    name="reset_mode"
                    selected={false}
                    disabled
                    disabledReason={t('console.accounts.resetModal.optionEmailDisabledTitle')}
                    onSelect={() => {}}
                    title={t('console.accounts.resetModal.optionEmailTitle')}
                    description={t('console.accounts.resetModal.optionEmailDesc')}
                />
            )}
        </fieldset>
    );
}

export default ResetCredentialFields;
